/**
 * Hook for location data: countries → states → cities.
 *
 * Countries: bundled local CSV (src/data/countries.csv) — always available,
 *            no external dependency (the old restcountries.com call failed
 *            silently in some networks, leaving the dropdown empty).
 * States:    CountriesNow API  (countriesnow.space/states) — no key needed
 * Cities:    CountriesNow API  (countriesnow.space/state/cities)
 */

import { useState, useEffect, useRef } from 'react';
import countriesCsvRaw from '../data/countries.csv?raw';

interface CountryOption {
  code: string;   // ISO 3166-1 alpha-2 (e.g. "BR")
  name: string;   // Common name (e.g. "Brazil")
}

// ─── Module-level caches ───────────────────────────────────────────────────────

const statesCache: Record<string, string[]> = {};
const citiesCache: Record<string, string[]> = {};

// ─── Countries (bundled local CSV) ───────────────────────────────────────────────

/** Minimal CSV line parser that handles double-quoted fields. */
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

/** Parse the bundled countries CSV (columns: iso2,country_name,...) once. */
function parseCountriesCsv(csv: string): CountryOption[] {
  const lines = csv.split(/\r?\n/).filter((l) => l.trim());
  const list: CountryOption[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]);
    const code = (cols[0] ?? '').trim();
    const name = (cols[1] ?? '').trim();
    if (code && name) list.push({ code, name });
  }
  return list.sort((a, b) => a.name.localeCompare(b.name));
}

const localCountries: CountryOption[] = parseCountriesCsv(countriesCsvRaw);

// ─── Fetch helpers ─────────────────────────────────────────────────────────────

async function fetchStates(countryName: string): Promise<string[]> {
  if (statesCache[countryName]) return statesCache[countryName];

  const res = await fetch('https://countriesnow.space/api/v0.1/countries/states', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ country: countryName }),
  });
  const data = await res.json();
  // Response: { data: { states: [{ name, state_code }] } }
  const states: string[] = ((data.data?.states ?? []) as { name: string }[])
    .map((s) => s.name)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
  statesCache[countryName] = states;
  return states;
}

async function fetchCities(countryName: string, stateName: string): Promise<string[]> {
  const key = `${countryName}||${stateName}`;
  if (citiesCache[key]) return citiesCache[key];

  const res = await fetch('https://countriesnow.space/api/v0.1/countries/state/cities', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ country: countryName, state: stateName }),
  });
  const data = await res.json();
  const cities: string[] = (data.data ?? [])
    .filter(Boolean)
    .sort((a: string, b: string) => a.localeCompare(b));
  citiesCache[key] = cities;
  return cities;
}

// ─── Hooks ─────────────────────────────────────────────────────────────────────

/** Returns the sorted list of countries (parsed once from the bundled CSV). */
export function useCountries() {
  return { countries: localCountries, isLoading: false };
}

/** Returns states for a given country name, refetches when country changes. */
export function useStates(countryName: string) {
  const [states, setStates] = useState<string[]>(
    countryName ? (statesCache[countryName] ?? []) : [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const prev = useRef('');

   
  // the same reasoning as the certificate hook: this effect owns an async
  // fetch, and the synchronous calls are either a cache hit answering
  // immediately or the reset that precedes the request. Both belong with the
  // fetch they serve.
  useEffect(() => {
    if (!countryName || countryName === prev.current) return;
    prev.current = countryName;

    if (statesCache[countryName]) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- see the note above
      setStates(statesCache[countryName]);
      return;
    }
    setIsLoading(true);
    setStates([]);
    fetchStates(countryName)
      .then(setStates)
      .catch(() => setStates([]))
      .finally(() => setIsLoading(false));
  }, [countryName]);

  return { states, isLoading };
}

/** Returns cities for a given country + state, refetches when either changes. */
export function useCities(countryName: string, stateName: string) {
  const key = `${countryName}||${stateName}`;
  const [cities, setCities] = useState<string[]>(
    countryName && stateName ? (citiesCache[key] ?? []) : [],
  );
  const [isLoading, setIsLoading] = useState(false);
  const prev = useRef('');

   
  // the same reasoning as the certificate hook: this effect owns an async
  // fetch, and the synchronous calls are either a cache hit answering
  // immediately or the reset that precedes the request. Both belong with the
  // fetch they serve.
  useEffect(() => {
    if (!countryName || !stateName || key === prev.current) return;
    prev.current = key;

    if (citiesCache[key]) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- see the note above
      setCities(citiesCache[key]);
      return;
    }
    setIsLoading(true);
    setCities([]);
    fetchCities(countryName, stateName)
      .then(setCities)
      .catch(() => setCities([]))
      .finally(() => setIsLoading(false));
  }, [countryName, stateName, key]);

  return { cities, isLoading };
}
